import React, { FC } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ToggleOffIcon from '@mui/icons-material/ToggleOff'
import {
  FormatInsertRule,
  soapSectionLabels,
} from '@/features/internal/format-insert-rules/types/formatInsertRule'

type Props = {
  rules: FormatInsertRule[] | undefined
  loading: boolean
  onEdit: (rule: FormatInsertRule) => void
  onDelete: (id: string) => void
  onToggleActive: (id: string) => void
}

const StyledCard = styled(Card)<{ isActive: boolean }>(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: theme.palette.background.paper,
  variants: [
    {
      props: ({ isActive }) => isActive,
      style: {
        border: {
          border: theme.palette.grey[400],
        },
      },
    },
    {
      props: ({ isActive }) => isActive,
      style: {
        backgroundColor: theme.palette.grey[50],
      },
    },
  ],
}))

const KeywordChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.25),
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.text.secondary,
  height: '24px',
}))

const SectionChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.grey[300],
  color: theme.palette.text.primary,
}))

const InsertTextBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  borderLeft: `3px solid ${theme.palette.grey[400]}`,
  marginTop: theme.spacing(1),
}))

const ActionButtonGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
})

export const FormatInsertRuleList: FC<Props> = ({
  rules,
  loading,
  onEdit,
  onDelete,
  onToggleActive,
}) => {
  if (loading) {
    return (
      <Box>
        {[1, 2, 3].map((i) => (
          <Card key={i} sx={{ mb: 2 }}>
            <CardContent>
              <Skeleton variant="text" width="30%" height={32} />
              <Skeleton variant="text" width="60%" height={20} sx={{ mt: 1 }} />
              <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        ))}
      </Box>
    )
  }

  return (
    <Box>
      {!rules || rules.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              まだルールが作成されていません
            </Typography>
            <Typography variant="body2" color="textSecondary">
              新規ルール追加ボタンからルールを作成してください
            </Typography>
          </CardContent>
        </Card>
      ) : (
        rules?.map((rule) => (
          <StyledCard key={rule.id} isActive={rule.is_active}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="h6">{rule.name}</Typography>
                    <SectionChip
                      label={soapSectionLabels[rule.target_section]}
                      size="small"
                    />
                    {rule.is_active ? (
                      <Chip
                        label="有効"
                        size="small"
                        sx={{
                          backgroundColor: 'grey.200',
                          color: 'text.primary',
                        }}
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        label="無効"
                        size="small"
                        sx={{
                          backgroundColor: 'grey.100',
                          color: 'text.secondary',
                        }}
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {rule.description && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {rule.description}
                    </Typography>
                  )}

                  <Box mb={1}>
                    <Typography variant="caption" color="text.secondary">
                      キーワード:
                    </Typography>
                    <Box mt={0.5}>
                      {rule.keywords?.map((keyword, index) => (
                        <KeywordChip
                          key={index}
                          label={keyword}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      挿入テキスト:
                    </Typography>
                    <InsertTextBox>
                      <Typography variant="body2">
                        {rule.insert_text}
                      </Typography>
                    </InsertTextBox>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    height="100%"
                    justifyContent="space-between"
                  >
                    <ActionButtonGroup>
                      <Tooltip
                        title={rule.is_active ? '無効化' : '有効化'}
                        arrow
                      >
                        <IconButton
                          onClick={() => rule.id && onToggleActive(rule.id)}
                          sx={{
                            color: rule.is_active
                              ? 'text.primary'
                              : 'text.secondary',
                          }}
                        >
                          {rule.is_active ? (
                            <ToggleOnIcon fontSize="large" />
                          ) : (
                            <ToggleOffIcon fontSize="large" />
                          )}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="編集" arrow>
                        <IconButton
                          onClick={() => onEdit(rule)}
                          sx={{
                            color: 'text.primary',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="削除" arrow>
                        <IconButton
                          onClick={() => rule.id && onDelete(rule.id)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ActionButtonGroup>

                    {rule.created_at && (
                      <Typography variant="caption" color="textSecondary">
                        作成日:{' '}
                        {new Date(rule.created_at).toLocaleDateString('ja-JP')}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>
        ))
      )}
    </Box>
  )
}
